import { API_URLS } from "@solomon-apps/shared";
import { Base58Util } from "@solomon-labs/base58";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { RememberedSquad } from "./retailAccountStore";

export interface RetailSquadsDiscoveryResult {
    multisigs: RememberedSquad[];
    partial: boolean;
}

export async function fetchRetailSquadsForSigner(
    signerAddress: string,
    signal?: AbortSignal
): Promise<RetailSquadsDiscoveryResult> {
    const response = await fetch(`${API_URLS.YAAS}/api/yaas/multisigs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signerAddress }),
        signal
    });
    if (!response.ok) throw new Error("Couldn't load your Squads wallets. You can retry or paste a vault address.");
    const payload: unknown = await response.json();
    if (!payload || typeof payload !== "object" || !("multisigs" in payload) || !Array.isArray(payload.multisigs)) {
        throw new Error("Couldn't load your Squads wallets. Please retry.");
    }
    if ("partial" in payload && typeof payload.partial !== "boolean") {
        throw new Error("Couldn't load your Squads wallets. Please retry.");
    }
    const multisigs = payload.multisigs.map((row: unknown) => {
        if (
            !row ||
            typeof row !== "object" ||
            !("address" in row) ||
            !("vaultAddress" in row) ||
            typeof row.address !== "string" ||
            typeof row.vaultAddress !== "string" ||
            !Base58Util.isValidPublicKey(row.address) ||
            !Base58Util.isValidPublicKey(row.vaultAddress)
        ) {
            throw new Error("Couldn't load your Squads wallets. Please retry.");
        }
        return { address: row.address, vaultAddress: row.vaultAddress };
    });
    // Older API deployments return a complete list without this additive field.
    return { multisigs, partial: "partial" in payload && payload.partial === true };
}

/** Discovery is keyed by signer, and never replaces the account's membership gate. */
export function useRetailSquadsDiscovery(
    signerAddress: string,
    enabled: boolean
): UseQueryResult<RetailSquadsDiscoveryResult, Error> {
    return useQuery({
        queryKey: ["yaas", "squads-for-signer", signerAddress],
        queryFn: ({ signal }) => fetchRetailSquadsForSigner(signerAddress, signal),
        enabled: enabled && !!signerAddress,
        staleTime: 30_000,
        retry: false
    });
}
